import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TipsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

/**
 * Modal réutilisable pour afficher des conseils/tips contextuels
 * S'affiche avec un fond flouté et une animation d'entrée
 *
 * IMPORTANT: Utilise createPortal pour rendre le modal dans document.body
 * afin d'éviter les problèmes de positionnement causés par les transforms CSS
 * des parents (comme animate-fade-up qui crée un nouveau stacking context)
 */
export function TipsModal({ isOpen, onClose, title = "Conseil", children }: TipsModalProps) {
  if (!isOpen) return null;

  // Utiliser un portal pour rendre le modal directement dans document.body
  // Cela évite les problèmes de positionnement fixed causés par les transforms des parents
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop flouté */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={cn(
          "relative bg-card border border-border/50 rounded-2xl shadow-2xl",
          "max-w-md w-full mx-4 p-6",
          "animate-in fade-in-0 zoom-in-95 duration-200"
        )}
      >
        {/* Header avec icône et titre */}
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-primary/10 rounded-lg">
            <span className="text-2xl">💡</span>
          </div>
          <h2 className="text-xl font-bold text-foreground">{title}</h2>
        </div>

        {/* Contenu */}
        <div className="tips-modal-content text-foreground/80">
          {children}
        </div>

        {/* Bouton de fermeture */}
        <Button
          className="w-full mt-6 font-semibold"
          onClick={onClose}
        >
          J'ai compris
        </Button>
      </div>
    </div>,
    document.body
  );
}

export default TipsModal;
